import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { crumb } from '../lib/feedback/recorder';
import { reportingTz } from '../lib/format';
import { useSession } from '../lib/session';
import { FeedbackButton } from './FeedbackButton';

const NAV: { to: string; label: string; built: boolean }[] = [
  { to: '/', label: 'Overview', built: true },
  { to: '/competitors', label: 'Competitors', built: true },
  { to: '/watchlist', label: 'Watch list', built: true },
  { to: '/pricing', label: 'Pricing', built: true },
  { to: '/content', label: 'Content Studio', built: true },
  { to: '/prospects', label: 'Prospects', built: true },
  { to: '/reports', label: 'Reports', built: true },
  { to: '/jobs', label: 'Jobs', built: true },
  { to: '/settings', label: 'Settings', built: true },
];

export function routeFileHint(pathname: string): string | null {
  if (pathname === '/') return 'web/src/pages/Overview.tsx';
  if (pathname === '/competitors/merge') return 'web/src/pages/Merge.tsx';
  if (/^\/competitors\/[^/]+/.test(pathname)) return 'web/src/pages/Studio.tsx';
  if (pathname.startsWith('/competitors')) return 'web/src/pages/Competitors.tsx';
  if (pathname.startsWith('/watchlist')) return 'web/src/pages/WatchList.tsx';
  if (pathname.startsWith('/jobs')) return 'web/src/pages/Jobs.tsx';
  if (pathname.startsWith('/settings')) return 'web/src/pages/Settings.tsx';
  if (pathname.startsWith('/pricing/quotations/')) return 'web/src/pages/pricing/QuotationReview.tsx';
  if (pathname.startsWith('/pricing')) return 'web/src/pages/pricing/Pricing.tsx';
  if (pathname.startsWith('/content/items/')) return 'web/src/pages/content/Editor.tsx';
  if (pathname.startsWith('/content/projects/')) return 'web/src/pages/content/ProjectPage.tsx';
  if (pathname.startsWith('/content')) return 'web/src/pages/content/ContentHome.tsx';
  if (pathname.startsWith('/reports')) return 'web/src/pages/Reports.tsx';
  if (/^\/prospects\/[^/]+/.test(pathname)) return 'web/src/pages/prospects/ProspectPage.tsx';
  if (pathname.startsWith('/prospects')) return 'web/src/pages/prospects/Prospects.tsx';
  return null;
}

export function Layout() {
  const { user, signOut } = useSession();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    crumb('navigate', location.pathname + location.search);
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div className="shell">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <span className="brand-name">
            Mirage <em>Marketing</em>
          </span>
        </div>
        <button type="button" className="btn ghost small menu-toggle" aria-expanded={menuOpen} aria-controls="primary-nav" onClick={() => setMenuOpen((o) => !o)}>
          Menu
        </button>
        <nav id="primary-nav" className={`primary-nav${menuOpen ? ' open' : ''}`} aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}${n.built ? '' : ' later'}`}>
              {n.label}
              {!n.built && <span className="sr-only"> (not built yet)</span>}
            </NavLink>
          ))}
        </nav>
        <div className="userbox">
          <span className="user-name" title={user?.email}>
            {user?.name} <span className="muted">· {user?.role}</span>
          </span>
          <button type="button" className="btn ghost small" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>
      <main id="main" className="main">
        <Outlet />
      </main>
      <footer className="footer muted small">
        <span>Mirage Visualisation · internal</span>
        <span>Times shown in {reportingTz()}</span>
      </footer>
      <FeedbackButton
        user={user ? { userId: user.id, email: user.email, role: user.role } : null}
        routeFileHint={() => routeFileHint(window.location.pathname)}
        getAppState={() => ({ search: window.location.search })}
      />
    </div>
  );
}
